import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';

const Footer = () => {
  const [logo, setLogo] = useState(null);
  const [collegeName, setCollegeName] = useState('Dolphin Institute');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'college_info');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const settings = settingsSnap.data();
          if (settings.logoUrl) setLogo(settings.logoUrl);
          if (settings.collegeName) setCollegeName(settings.collegeName);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          {/* Brand Section */}
          <div className="footer-brand">
            <div className="footer-logo">
              {logo ? (
                <img src={logo} alt={collegeName} className="footer-logo-img" />
              ) : (
                <div className="footer-logo-text">{collegeName}</div>
              )}
            </div>
            <p className="footer-description">
              Excellence in Medical Sciences & Technology Education. 
              Shaping future healthcare leaders since 2008.
            </p>
            <div className="footer-social">
              {[
                { icon: 'facebook-f', color: '#1877f2', url: '#' },
                { icon: 'twitter', color: '#1da1f2', url: '#' },
                { icon: 'instagram', color: '#e4405f', url: '#' },
                { icon: 'linkedin-in', color: '#0077b5', url: '#' },
                { icon: 'youtube', color: '#ff0000', url: '#' },
                { icon: 'whatsapp', color: '#25D366', url: 'https://wa.me/919906067123' }
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-icon"
                  style={{ '--hover-color': social.color }}
                  aria-label={social.icon}
                >
                  <i className={`fab fa-${social.icon}`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-links-section">
            <h4>Quick Links</h4>
            <ul>
              <li><Link to="/">Home</Link></li>
              <li><Link to="/courses">Courses</Link></li>
              <li><Link to="/departments">Departments</Link></li>
              <li><Link to="/faculty">Faculty</Link></li>
              <li><Link to="/gallery">Gallery</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div className="footer-links-section">
            <h4>Resources</h4>
            <ul>
              <li><a href="#">Admissions</a></li>
              <li><a href="#">Scholarships</a></li>
              <li><a href="#">Campus Life</a></li>
              <li><a href="#">Library</a></li>
              <li><a href="#">Career Services</a></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="footer-contact">
            <h4>Get in Touch</h4>
            <ul>
              <li>
                <i className="fas fa-map-marker-alt"></i>
                <span>Alamdar Colony Trichal Pulwama-192301</span>
              </li>
              <li>
                <i className="fas fa-phone"></i>
                <span>01933294429|01933294409|01933294419</span>
              </li>
              <li>
                <i className="fas fa-envelope"></i>
                <span>info@dimst.in</span>
              </li>
              <li>
                <i className="far fa-clock"></i>
                <span>Mon-Sat: 9:00 AM - 6:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <p>
              &copy; {currentYear} {collegeName}. All rights reserved.
            </p>
          </div>
          <div className="footer-bottom-center">
            <p className="footer-developed-by">
              Developed by{' '}
              <a 
                href="https://thundercodetechnologies.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="developer-link"
              >
                <i className="fas fa-code"></i> Thundercode Technologies
              </a>
            </p>
          </div>
          <div className="footer-bottom-links">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
            <a href="#">Sitemap</a>
          </div>
        </div>
      </div>

      {/* Add CSS for responsiveness */}
      <style>{`
        .footer {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          margin-top: 50px;
          padding: 60px 0 30px;
          position: relative;
          overflow: hidden;
        }

        /* Subtle decorative line at top */
        .footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--clr-blue), var(--clr-purple), var(--clr-emerald));
        }

        /* Footer Grid */
        .footer-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1.5fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        /* Brand Section */
        .footer-brand {
          display: flex;
          flex-direction: column;
        }

        .footer-logo {
          margin-bottom: 18px;
        }

        .footer-logo-img {
          height: 50px;
          width: auto;
          object-fit: contain;
        }

        .footer-logo-text {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
        }

        .footer-description {
          color: #94a3b8;
          font-size: 0.9rem;
          line-height: 1.7;
          margin-bottom: 20px;
          max-width: 350px;
        }

        .footer-social {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .footer-social-icon {
          width: 38px;
          height: 38px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          color: white;
          text-decoration: none;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .footer-social-icon:hover {
          background: var(--hover-color, var(--clr-blue));
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
          border-color: transparent;
        }

        /* Links Sections */
        .footer-links-section h4,
        .footer-contact h4 {
          font-family: 'Playfair Display', serif;
          font-size: 1.1rem;
          margin-bottom: 20px;
          color: white;
          position: relative;
          padding-bottom: 12px;
        }

        .footer-links-section h4::after,
        .footer-contact h4::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 30px;
          height: 2px;
          background: var(--clr-blue);
        }

        .footer-links-section ul,
        .footer-contact ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .footer-links-section ul li,
        .footer-contact ul li {
          margin-bottom: 12px;
        }

        .footer-links-section ul li a {
          color: #94a3b8;
          text-decoration: none;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          display: inline-block;
        }

        .footer-links-section ul li a:hover {
          color: var(--clr-blue);
          transform: translateX(5px);
        }

        .footer-contact ul li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          color: #94a3b8;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .footer-contact ul li i {
          color: var(--clr-blue);
          width: 18px;
          margin-top: 3px;
          flex-shrink: 0;
        }

        .footer-contact ul li span {
          flex: 1;
        }

        /* Bottom Bar */
        .footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 25px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          font-size: 0.85rem;
          color: #94a3b8;
          flex-wrap: wrap;
          gap: 15px;
        }

        .footer-bottom-left {
          flex: 1;
        }

        .footer-bottom-center {
          flex: 1;
          text-align: center;
        }

        .footer-developed-by {
          font-size: 0.85rem;
          color: #94a3b8;
        }

        .developer-link {
          color: #94a3b8;
          text-decoration: none;
          transition: all 0.3s ease;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .developer-link i {
          color: var(--clr-blue);
        }

        .developer-link:hover {
          color: var(--clr-blue);
          transform: scale(1.05);
        }

        .footer-bottom-links {
          display: flex;
          gap: 25px;
          flex: 1;
          justify-content: flex-end;
        }

        .footer-bottom-links a {
          color: #94a3b8;
          text-decoration: none;
          transition: all 0.3s ease;
          font-size: 0.8rem;
        }

        .footer-bottom-links a:hover {
          color: var(--clr-blue);
        }

        /* Tablet Responsive */
        @media (max-width: 1024px) {
          .footer-grid {
            grid-template-columns: 1.5fr 1fr 1fr 1.2fr;
            gap: 30px;
          }

          .footer-bottom {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }

          .footer-bottom-left,
          .footer-bottom-center,
          .footer-bottom-links {
            flex: none;
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .footer {
            padding: 40px 0 25px;
          }

          .footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 30px;
            margin-bottom: 30px;
          }

          .footer-brand {
            grid-column: 1 / -1;
          }

          .footer-description {
            max-width: 100%;
          }

          .footer-logo-img {
            height: 45px;
          }

          .footer-logo-text {
            font-size: 1.3rem;
          }

          .footer-bottom {
            flex-direction: column;
            text-align: center;
            gap: 12px;
            padding-top: 20px;
          }

          .footer-bottom-links {
            gap: 20px;
            flex-wrap: wrap;
            justify-content: center;
          }

          .footer-contact ul li {
            font-size: 0.85rem;
          }

          .footer-developed-by {
            font-size: 0.8rem;
          }
        }

        @media (max-width: 480px) {
          .footer {
            padding: 30px 0 20px;
            margin-top: 30px;
          }

          .footer-grid {
            grid-template-columns: 1fr;
            gap: 25px;
            margin-bottom: 25px;
          }

          .footer-brand {
            text-align: center;
          }

          .footer-logo {
            display: flex;
            justify-content: center;
          }

          .footer-logo-img {
            height: 40px;
          }

          .footer-logo-text {
            font-size: 1.2rem;
          }

          .footer-description {
            font-size: 0.85rem;
            text-align: center;
          }

          .footer-social {
            justify-content: center;
          }

          .footer-social-icon {
            width: 40px;
            height: 40px;
          }

          .footer-links-section h4,
          .footer-contact h4 {
            font-size: 1rem;
            text-align: center;
          }

          .footer-links-section h4::after,
          .footer-contact h4::after {
            left: 50%;
            transform: translateX(-50%);
          }

          .footer-links-section ul {
            text-align: center;
          }

          .footer-links-section ul li a:hover {
            transform: translateX(0);
          }

          .footer-contact ul li {
            justify-content: center;
            font-size: 0.8rem;
          }

          .footer-contact ul li i {
            margin-top: 2px;
          }

          .footer-bottom {
            font-size: 0.75rem;
            padding-top: 18px;
          }

          .footer-developed-by {
            font-size: 0.75rem;
          }

          .footer-bottom-links {
            gap: 15px;
          }

          .footer-bottom-links a {
            font-size: 0.7rem;
          }
        }

        @media (max-width: 360px) {
          .footer-social-icon {
            width: 36px;
            height: 36px;
            font-size: 0.8rem;
          }

          .footer-contact ul li {
            font-size: 0.75rem;
          }

          .footer-bottom-links {
            gap: 12px;
          }

          .footer-bottom-links a {
            font-size: 0.65rem;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;